import { useState, useMemo, useRef, useEffect } from "react"
import { Check, ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface SearchableSelectOption {
  id: string
  name: string
  color?: string
}

interface SearchableSelectProps {
  value: string
  onChange: (value: string) => void
  options: SearchableSelectOption[]
  placeholder?: string
  emptyLabel?: string
  className?: string
  error?: boolean
  allowEmpty?: boolean
}

function normalizeString(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "-",
  emptyLabel = "-",
  className,
  error,
  allowEmpty = true,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const inputRef = useRef<HTMLInputElement>(null)

  const allOptions: SearchableSelectOption[] = useMemo(
    () => allowEmpty ? [{ id: "", name: emptyLabel }, ...options] : options,
    [allowEmpty, emptyLabel, options]
  )

  const selectedOption = allOptions.find((o) => o.id === value)

  const filteredOptions = useMemo(() => {
    if (!search) return allOptions
    return allOptions.filter((o) =>
      normalizeString(o.name).includes(normalizeString(search))
    )
  }, [search, allOptions])

  useEffect(() => {
    if (open && inputRef.current) {
      // Small delay to ensure popover is mounted
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  function handleSelect(optionId: string) {
    onChange(optionId)
    setOpen(false)
    setSearch("")
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={true}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between font-normal",
            error && "border-destructive focus:ring-destructive",
            className
          )}
        >
          {selectedOption?.name || placeholder}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          inputRef.current?.focus()
        }}
      >
        <div className="p-2 border-b">
          <input
            ref={inputRef}
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex h-8 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            onKeyDown={(e) => {
              // Prevent form submission
              if (e.key === "Enter") {
                e.preventDefault()
                if (filteredOptions.length === 1) {
                  handleSelect(filteredOptions[0].id)
                }
              }
            }}
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {filteredOptions.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              Nenhum resultado encontrado.
            </div>
          ) : (
            filteredOptions.map((option) => (
              <button
                key={option.id || "_empty"}
                type="button"
                onClick={() => handleSelect(option.id)}
                className={cn(
                  "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                  value === option.id && "bg-accent"
                )}
              >
                <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                  {value === option.id && <Check className="h-4 w-4" />}
                </span>
                {option.name}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
